import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { usePathname } from "next/navigation";
import { useConversation, type ConversationStatus } from "@elevenlabs/react";
import { useMessageHistory } from "./useMessageHistory";
import type { AddMessageInput } from "@/types/agent/message";
import type { StatusEvent } from "@/types/agent";
import { useScroller } from "@/lib/hooks";
import { pushToDataLayer } from "@/lib/analytics/pushToDataLayer";
import { buildHandoffUpdate, classifyMicError, isInternalMessage, isVoiceUnsupported, normalizeUserText } from "../voice";
import { getAgentConsent, grantAgentConsent, subscribeToAgentConsent } from "../consent";
import { handleToolCallEvent } from "../handlers";
import type { StreamEventHandlerDeps, SendUserMessageOptions } from "../handlers";
import { getToolCallResponseType, trackAgentResponse } from "../analytics";
import { agentStore, registerReportPageNotFound, registerSendUserMessage, useAgentStore } from "../store/agentStore";
import { getCurrentPage } from "@/lib/utils/getCurrentPage";
import { getTranslations, type Locale } from "@/lib/i18n/config";
const TOKEN_ENDPOINT = "/api/elevenlabs/token";
const TAG_ENDPOINT = "/api/elevenlabs/tag";

/** Response of `GET /api/elevenlabs/token`, one shape per connection type. */
interface SessionCredentials {
  signedUrl?: string;
  conversationToken?: string;
  serverLocation?: string;
}
const getAvailableSections = (): string[] => typeof document === "undefined" ? [] : Array.from(document.querySelectorAll("section[id]")).map(el => el.id);

/**
 * Builds a short contextual-update string with the current page state.
 *
 * ElevenLabs accepts `dynamic_variables` only in the connection-init message;
 * they're immutable for the conversation's lifetime. To keep the agent aware of
 * the current page/sections after client-side navigation we push this snapshot
 * via `sendContextualUpdate` before every user message.
 */
const buildContextUpdate = (): string => {
  const page = getCurrentPage();
  const sections = getAvailableSections();
  return `Aktueller Seiten-Kontext — current_page: "${page || "home"}"; ` + `available_sections: ${sections.length ? sections.join(", ") : "keine"}.`;
};
const consentOnServer = (): boolean => false;

/** A message held back until the visitor has agreed (see `../consent`). */
interface PendingConsentMessage {
  content: string;
  preventAddTextToHistory?: boolean;
  sendOptions?: SendUserMessageOptions;
}
const getAttachmentNameFromOptions = (options?: SendUserMessageOptions): string | undefined => {
  if (!options?.formSubmission) {
    return undefined;
  }
  for (const value of Object.values(options.formSubmission.data)) {
    if (value instanceof File) return value.name;
    if (Array.isArray(value) && value.length > 0 && value[0] instanceof File) {
      return value.map(f => f.name).join(", ");
    }
  }
  return undefined;
};
interface UseAgentControllerOptions {
  initialMessages?: AddMessageInput[];
  locale?: Locale;
}

/**
 * Status row shown in the chat log. The SSE-era `StatusEvent` steps plus the two
 * voice states, which are derived from the live session rather than set.
 */
export type AgentStatusStep = StatusEvent["step"] | "listening" | "speaking";
interface AgentControllerState {
  isOpen: boolean;
  /** True while the "start a new conversation?" confirmation card is showing. */
  isNewSessionDialogOpen: boolean;
  /** Mobile bottom sheet after an agent navigation (MWR-609). Inert on lg+. */
  isCollapsed: boolean;
  statusStep: AgentStatusStep | null;
  /** ElevenLabs connection status. */
  connectionStatus: ConversationStatus;
  /** True while the agent is speaking (voice mode). */
  isSpeaking: boolean;
  /** True while the session runs in voice (speech-to-speech) mode. */
  isVoice: boolean;
  /**
   * True while the panel is open and the visitor has not agreed to their input
   * being processed yet. The footer renders the consent gate instead of the
   * input while this holds.
   */
  needsConsent: boolean;
  messageHistory: ReturnType<typeof useMessageHistory>;
}
interface AgentControllerActions {
  close: () => void;
  open: () => void;
  openNewSessionDialog: () => void;
  closeNewSessionDialog: () => void;
  /** Restores the full-height panel from the mobile bottom sheet. */
  expand: () => void;
  startNewSession: () => void;
  sendUserMessage: (content: string, preventAddTextToHistory?: boolean, options?: SendUserMessageOptions) => void;
  /** Enters or leaves voice mode (restarts the ElevenLabs session). */
  toggleVoice: () => void;
  /** Records consent and sends whatever was held back waiting for it. */
  grantConsent: () => void;
  /** Explains what declining costs; the gate stays up. */
  declineConsent: () => void;
}
export interface AgentController {
  state: AgentControllerState;
  actions: AgentControllerActions;
}

/**
 * Central controller for Agent UI state and message interactions.
 *
 * Backed by ElevenLabs Conversational AI (`@elevenlabs/react`). Must be rendered
 * inside a `<ConversationProvider>` (see `Agent.tsx`).
 *
 * Text rendering, tool calls (navigation, forms, buttons) and the surrounding UI
 * are unchanged from the previous custom-backend implementation; only the
 * transport (custom SSE → ElevenLabs session) and the event wiring changed.
 */
export const useAgentController = t0 => {
  const $ = _c(128);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {
      initialMessages: [{
        type: "text",
        sender: "assistant",
        content: "Hallo! Wie kann ich dir heute helfen?"
      }]
    } : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  const t2 = options.locale ?? "de";
  let t3;
  if ($[2] !== t2) {
    t3 = getTranslations(t2);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const {
    agent: t
  } = t3;
  const messageHistory = useMessageHistory();
  const {
    scrollToAnchor
  } = useScroller();
  const hasAppliedInitialMessages = useRef(false);
  const streamingMessageIdRef = useRef(null);
  const streamingContentRef = useRef("");
  const sendUserMessageRef = useRef(null);
  const {
    isOpen,
    isNewSessionDialogOpen,
    isCollapsed
  } = useAgentStore();
  const [statusStep, setStatusStep] = useState(null);
  const phaseRef = useRef("idle");
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const pendingMessagesRef = useRef(t4);
  const statusRef = useRef("disconnected");
  const [isVoice, setIsVoice] = useState(false);
  const voiceRef = useRef(false);
  const pendingContextRef = useRef(null);
  const voiceStartedAtRef = useRef(null);
  const hasShownVoiceNoticeRef = useRef(false);
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const localUserTextsRef = useRef(t5);
  const hasShownExpiryNoticeRef = useRef(false);
  const hasConsent = useSyncExternalStore(subscribeToAgentConsent, getAgentConsent, consentOnServer);
  const pendingConsentMessageRef = useRef(null);
  const hasShownConsentAskRef = useRef(false);
  const hasShownDeclineHintRef = useRef(false);
  let t6;
  if ($[6] !== messageHistory || $[7] !== scrollToAnchor || $[8] !== t) {
    t6 = {
      messageHistory,
      scrollToAnchor,
      sendUserMessageRef,
      t
    };
    $[6] = messageHistory;
    $[7] = scrollToAnchor;
    $[8] = t;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const depsRef = useRef(t6);
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      streamingMessageIdRef.current = null;
      streamingContentRef.current = "";
    };
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const resetStreaming = t7;
  let t8;
  if ($[11] !== messageHistory) {
    t8 = delta => {
      streamingContentRef.current = streamingContentRef.current + delta;
      if (streamingMessageIdRef.current === null) {
        const msg = messageHistory.addTextMessage("assistant", streamingContentRef.current);
        streamingMessageIdRef.current = msg.id;
        trackAgentResponse("default");
      } else {
        messageHistory.updateMessage(streamingMessageIdRef.current, {
          content: streamingContentRef.current
        });
      }
      setStatusStep(null);
    };
    $[11] = messageHistory;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const appendAssistantDelta = t8;
  let t9;
  if ($[13] !== messageHistory) {
    t9 = finalText => {
      if (streamingMessageIdRef.current !== null) {
        messageHistory.updateMessage(streamingMessageIdRef.current, {
          content: finalText
        });
      } else {
        messageHistory.addTextMessage("assistant", finalText);
        trackAgentResponse("default");
      }
      resetStreaming();
      setStatusStep(null);
    };
    $[13] = messageHistory;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const finalizeAssistantMessage = t9;
  let t10;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = reason => {
      if (!voiceRef.current) {
        return;
      }
      voiceRef.current = false;
      setIsVoice(false);
      localUserTextsRef.current = [];
      setStatusStep(null);
      const startedAt = voiceStartedAtRef.current;
      voiceStartedAtRef.current = null;
      pushToDataLayer({
        event: "agent_voice_end",
        reason,
        duration_seconds: startedAt ? Math.round((Date.now() - startedAt) / 1000) : 0
      });
    };
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const endVoiceState = t10;
  let t11;
  if ($[16] !== messageHistory || $[17] !== t.sessionExpired || $[18] !== t.sessionExpiredHint) {
    t11 = () => {
      if (hasShownExpiryNoticeRef.current) {
        return;
      }
      hasShownExpiryNoticeRef.current = true;
      messageHistory.addSystemMessage(t.sessionExpired);
      messageHistory.addSystemMessage(t.sessionExpiredHint);
    };
    $[16] = messageHistory;
    $[17] = t.sessionExpired;
    $[18] = t.sessionExpiredHint;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const notifyConversationExpired = t11;
  let t12;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = (name, args, confirmation) => {
      const result = handleToolCallEvent({
        type: "tool_call",
        name,
        args
      } as unknown as Parameters<typeof handleToolCallEvent>[0], depsRef.current);
      if (name !== "navigate" && name !== "navigate_and_scroll") {
        trackAgentResponse(getToolCallResponseType(name as Parameters<typeof getToolCallResponseType>[0]));
      }
      return result ?? confirmation;
    };
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const dispatch = t12;
  let t13;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      navigate: args_0 => dispatch("navigate", args_0, "navigation shown"),
      navigate_and_scroll: args_1 => dispatch("navigate_and_scroll", args_1, "navigation shown"),
      scroll: args_2 => dispatch("scroll", args_2, "scrolled"),
      show_dynamic_form: args_3 => dispatch("show_dynamic_form", args_3, "form shown"),
      show_action_buttons: args_4 => dispatch("show_action_buttons", args_4, "buttons shown"),
      suggest_appointment_dates: args_5 => dispatch("suggest_appointment_dates", args_5, "dates shown"),
      show_lead_contact_form: args_6 => dispatch("show_lead_contact_form", args_6, "form shown"),
      show_application_form: args_7 => dispatch("show_application_form", args_7, "form shown"),
      show_document_upload_form: args_8 => dispatch("show_document_upload_form", args_8, "form shown")
    };
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  const clientTools = t13;
  const t14 = !isVoice;
  let t15;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = t16 => {
      const {
        conversationId
      } = t16;
      statusRef.current = "connected";
      phaseRef.current = "live";
      hasShownExpiryNoticeRef.current = false;
      fetch(TAG_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          conversationId
        })
      }).catch(_temp);
      const carried = pendingContextRef.current;
      pendingContextRef.current = null;
      if (carried) {
        conversationRef.current?.sendContextualUpdate(carried);
      }
      const queued = pendingMessagesRef.current;
      pendingMessagesRef.current = [];
      queued.forEach(text => {
        conversationRef.current?.sendContextualUpdate(buildContextUpdate());
        conversationRef.current?.sendUserMessage(text);
      });
    };
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== messageHistory || $[24] !== notifyConversationExpired || $[25] !== t.voice) {
    t16 = details => {
      statusRef.current = "disconnected";
      if (details.reason === "user") {
        return;
      }
      phaseRef.current = "idle";
      pendingMessagesRef.current = [];
      resetStreaming();
      setStatusStep(null);
      if (voiceRef.current) {
        messageHistory.addTextMessage("assistant", t.voice.ended);
        endVoiceState(details.reason);
        return;
      }
      notifyConversationExpired();
    };
    $[23] = messageHistory;
    $[24] = notifyConversationExpired;
    $[25] = t.voice;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = t18 => {
      const {
        status
      } = t18;
      statusRef.current = status;
    };
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== messageHistory || $[29] !== t.errorMessage || $[30] !== t.voice) {
    t18 = (message, context) => {
      console.error("ElevenLabs agent error:", message, context);
      if (phaseRef.current === "switching") {
        return;
      }
      phaseRef.current = "idle";
      pendingMessagesRef.current = [];
      resetStreaming();
      if (voiceRef.current) {
        messageHistory.addTextMessage("assistant", t.voice[classifyMicError(context)]);
        endVoiceState("error");
      } else {
        messageHistory.addTextMessage("assistant", t.errorMessage);
      }
      setStatusStep(null);
    };
    $[28] = messageHistory;
    $[29] = t.errorMessage;
    $[30] = t.voice;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== appendAssistantDelta) {
    t19 = t20 => {
      const {
        text: text_0
      } = t20;
      if (text_0) {
        appendAssistantDelta(text_0);
      }
    };
    $[32] = appendAssistantDelta;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = () => {
      resetStreaming();
    };
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  let t21;
  if ($[35] !== finalizeAssistantMessage || $[36] !== messageHistory) {
    t21 = t22 => {
      const {
        message: message_0,
        role
      } = t22;
      if (!message_0) {
        return;
      }
      if (role === "user") {
        if (!voiceRef.current) {
          return;
        }
        if (isInternalMessage(message_0)) {
          return;
        }
        const echoIndex = localUserTextsRef.current.indexOf(normalizeUserText(message_0));
        if (echoIndex !== -1) {
          localUserTextsRef.current.splice(echoIndex, 1);
          return;
        }
        resetStreaming();
        messageHistory.addTextMessage("user", message_0);
        return;
      }
      finalizeAssistantMessage(message_0);
    };
    $[35] = finalizeAssistantMessage;
    $[36] = messageHistory;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  let t22;
  if ($[38] !== t14 || $[39] !== t16 || $[40] !== t18 || $[41] !== t19 || $[42] !== t21) {
    t22 = {
      textOnly: t14,
      clientTools,
      onConnect: t15,
      onDisconnect: t16,
      onStatusChange: t17,
      onError: t18,
      onAgentChatResponsePart: t19,
      onInterruption: t20,
      onMessage: t21
    };
    $[38] = t14;
    $[39] = t16;
    $[40] = t18;
    $[41] = t19;
    $[42] = t21;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  const conversation = useConversation(t22);
  const conversationRef = useRef(conversation);
  let t23;
  if ($[44] !== conversation || $[45] !== messageHistory || $[46] !== scrollToAnchor || $[47] !== t) {
    t23 = () => {
      conversationRef.current = conversation;
      depsRef.current = {
        messageHistory,
        scrollToAnchor,
        sendUserMessageRef,
        t
      };
    };
    $[44] = conversation;
    $[45] = messageHistory;
    $[46] = scrollToAnchor;
    $[47] = t;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  useEffect(t23);
  const buildDynamicVariables = _temp2;
  const mintCredentials = _temp3;
  let t24;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = (voice_0, credentials_0) => {
      phaseRef.current = "starting";
      if (voice_0) {
        conversationRef.current.startSession({
          conversationToken: credentials_0.conversationToken,
          connectionType: "webrtc",
          textOnly: false,
          serverLocation: credentials_0.serverLocation,
          useWakeLock: true,
          dynamicVariables: buildDynamicVariables()
        });
        return;
      }
      conversationRef.current.startSession({
        signedUrl: credentials_0.signedUrl,
        connectionType: "websocket",
        textOnly: true,
        dynamicVariables: buildDynamicVariables()
      });
    };
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  const connect = t24;
  let t25;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = async () => {
      if (phaseRef.current !== "idle") {
        return;
      }
      phaseRef.current = "switching";
      ;
      try {
        const credentials_1 = await mintCredentials(voiceRef.current);
        if (!agentStore.getState().isOpen) {
          phaseRef.current = "idle";
          return;
        }
        connect(voiceRef.current, credentials_1);
      } catch (t26) {
        const error = t26;
        phaseRef.current = "idle";
        throw error;
      }
    };
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  const ensureSession = t25;
  let t26;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = () => {
      let id;
      try {
        id = conversationRef.current.getId();
      } catch {
        return null;
      }
      return id || null;
    };
    $[51] = t26;
  } else {
    t26 = $[51];
  }
  const currentConversationId = t26;
  let t27;
  if ($[52] !== messageHistory) {
    t27 = () => {
      pendingContextRef.current = buildHandoffUpdate({
        pageContext: buildContextUpdate(),
        previousConversationId: currentConversationId(),
        history: messageHistory.getConversationHistory()
      });
    };
    $[52] = messageHistory;
    $[53] = t27;
  } else {
    t27 = $[53];
  }
  const queueHandoffContext = t27;
  let t28;
  if ($[54] !== messageHistory || $[55] !== queueHandoffContext || $[56] !== t.voice) {
    t28 = async voice_1 => {
      if (voice_1 === voiceRef.current) {
        return;
      }
      if (phaseRef.current === "switching" || phaseRef.current === "starting") {
        return;
      }
      if (voice_1 && !getAgentConsent()) {
        return;
      }
      if (!voice_1) {
        queueHandoffContext();
        phaseRef.current = "switching";
        conversationRef.current.endSession();
        pendingMessagesRef.current = [];
        resetStreaming();
        setStatusStep(null);
        phaseRef.current = "idle";
        endVoiceState("user");
        return;
      }
      if (isVoiceUnsupported()) {
        messageHistory.addTextMessage("assistant", t.voice.insecure);
        return;
      }
      try {
        const permission = await navigator.permissions.query({
          name: "microphone" as PermissionName
        });
        if (permission.state === "denied") {
          messageHistory.addTextMessage("assistant", t.voice.blocked);
          pushToDataLayer({
            event: "agent_voice_denied"
          });
          return;
        }
      } catch {}
      pushToDataLayer({
        event: "agent_voice_start"
      });
      if (!hasShownVoiceNoticeRef.current) {
        hasShownVoiceNoticeRef.current = true;
        messageHistory.addTextMessage("assistant", t.voice.privacyNotice);
      }
      ;
      try {
        const credentials_2 = await mintCredentials(true);
        if (!agentStore.getState().isOpen) {
          return;
        }
        queueHandoffContext();
        voiceRef.current = true;
        setIsVoice(true);
        voiceStartedAtRef.current = Date.now();
        setStatusStep(null);
        phaseRef.current = "switching";
        conversationRef.current.endSession();
        pendingMessagesRef.current = [];
        resetStreaming();
        connect(true, credentials_2);
      } catch (t29) {
        const error_0 = t29;
        console.error("Error starting ElevenLabs voice session:", error_0);
        phaseRef.current = "idle";
        const kind = classifyMicError(error_0);
        messageHistory.addTextMessage("assistant", t.voice[kind]);
        pushToDataLayer({
          event: "agent_voice_error",
          error_kind: kind
        });
        endVoiceState("error");
      }
    };
    $[54] = messageHistory;
    $[55] = queueHandoffContext;
    $[56] = t.voice;
    $[57] = t28;
  } else {
    t28 = $[57];
  }
  const setVoiceMode = t28;
  let t29;
  if ($[58] !== setVoiceMode) {
    t29 = () => {
      setVoiceMode(!voiceRef.current);
    };
    $[58] = setVoiceMode;
    $[59] = t29;
  } else {
    t29 = $[59];
  }
  const toggleVoice = t29;
  let t30;
  let t31;
  if ($[60] !== messageHistory || $[61] !== options.initialMessages) {
    t30 = () => {
      if (hasAppliedInitialMessages.current || !options.initialMessages?.length) {
        return;
      }
      options.initialMessages.forEach(message_1 => {
        if (message_1.type === "text") {
          messageHistory.addTextMessage(message_1.sender, message_1.content);
        }
        if (message_1.type === "navigate") {
          messageHistory.addNavigationMessage(message_1);
        }
      });
      hasAppliedInitialMessages.current = true;
    };
    t31 = [messageHistory, options.initialMessages];
    $[60] = messageHistory;
    $[61] = options.initialMessages;
    $[62] = t30;
    $[63] = t31;
  } else {
    t30 = $[62];
    t31 = $[63];
  }
  useEffect(t30, t31);
  let t32;
  if ($[64] !== hasConsent || $[65] !== isOpen || $[66] !== messageHistory || $[67] !== t.consent) {
    t32 = () => {
      if (!isOpen || hasConsent || hasShownConsentAskRef.current) {
        return;
      }
      hasShownConsentAskRef.current = true;
      messageHistory.addTextMessage("assistant", t.consent.question);
    };
    $[64] = hasConsent;
    $[65] = isOpen;
    $[66] = messageHistory;
    $[67] = t.consent;
    $[68] = t32;
  } else {
    t32 = $[68];
  }
  let t33;
  if ($[69] !== hasConsent || $[70] !== isOpen || $[71] !== messageHistory || $[72] !== t) {
    t33 = [hasConsent, isOpen, messageHistory, t];
    $[69] = hasConsent;
    $[70] = isOpen;
    $[71] = messageHistory;
    $[72] = t;
    $[73] = t33;
  } else {
    t33 = $[73];
  }
  useEffect(t32, t33);
  let t34;
  let t35;
  if ($[74] !== isOpen) {
    t34 = () => {
      if (isOpen) {
        document.body.setAttribute("data-agent-open", "true");
      } else {
        document.body.removeAttribute("data-agent-open");
      }
      return _temp4;
    };
    t35 = [isOpen];
    $[74] = isOpen;
    $[75] = t34;
    $[76] = t35;
  } else {
    t34 = $[75];
    t35 = $[76];
  }
  useEffect(t34, t35);
  let t36;
  let t37;
  if ($[77] !== isOpen) {
    t36 = () => {
      const shouldEnd = !isOpen || voiceRef.current;
      if (!shouldEnd || phaseRef.current === "idle") {
        return;
      }
      conversationRef.current.endSession();
      phaseRef.current = "idle";
      pendingMessagesRef.current = [];
      resetStreaming();
      endVoiceState(isOpen ? "minimized" : "panel_closed");
    };
    t37 = [endVoiceState, isOpen, resetStreaming];
    $[77] = isOpen;
    $[78] = t36;
    $[79] = t37;
  } else {
    t36 = $[78];
    t37 = $[79];
  }
  useEffect(t36, t37);
  let t38;
  let t39;
  if ($[80] !== setVoiceMode) {
    t38 = () => {
      const handleVisibility = () => {
        if (document.hidden && voiceRef.current) {
          setVoiceMode(false);
        }
      };
      const handlePageHide = () => {
        if (phaseRef.current !== "idle") {
          conversationRef.current.endSession();
        }
      };
      const handleKeyDown = event => {
        if (event.key !== "Escape" || !agentStore.getState().isOpen) {
          return;
        }
        if (voiceRef.current) {
          setVoiceMode(false);
          return;
        }
        agentStore.close();
      };
      document.addEventListener("visibilitychange", handleVisibility);
      window.addEventListener("pagehide", handlePageHide);
      window.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("visibilitychange", handleVisibility);
        window.removeEventListener("pagehide", handlePageHide);
        window.removeEventListener("keydown", handleKeyDown);
      };
    };
    t39 = [setVoiceMode];
    $[80] = setVoiceMode;
    $[81] = t38;
    $[82] = t39;
  } else {
    t38 = $[81];
    t39 = $[82];
  }
  useEffect(t38, t39);
  const pathname = usePathname();
  let t40;
  if ($[83] !== isVoice) {
    t40 = () => {
      if (!isVoice || statusRef.current !== "connected") {
        return;
      }
      conversationRef.current.sendContextualUpdate(buildContextUpdate());
    };
    $[83] = isVoice;
    $[84] = t40;
  } else {
    t40 = $[84];
  }
  let t41;
  if ($[85] !== isVoice || $[86] !== pathname) {
    t41 = [isVoice, pathname];
    $[85] = isVoice;
    $[86] = pathname;
    $[87] = t41;
  } else {
    t41 = $[87];
  }
  useEffect(t40, t41);
  const close = _temp5;
  const open = _temp6;
  const openNewSessionDialog = _temp7;
  const closeNewSessionDialog = _temp8;
  const expand = _temp9;
  let t42;
  if ($[88] !== messageHistory || $[89] !== options.initialMessages || $[90] !== t.consent || $[91] !== t.newSession) {
    t42 = () => {
      if (phaseRef.current !== "idle") {
        conversationRef.current.endSession();
      }
      phaseRef.current = "idle";
      statusRef.current = "disconnected";
      pendingContextRef.current = null;
      pendingMessagesRef.current = [];
      resetStreaming();
      setStatusStep(null);
      endVoiceState("new_session");
      messageHistory.clearMessages();
      messageHistory.addSystemMessage(t.newSession.started);
      options.initialMessages?.forEach(message_2 => {
        if (message_2.type === "text") {
          messageHistory.addTextMessage(message_2.sender, message_2.content);
        }
        if (message_2.type === "navigate") {
          messageHistory.addNavigationMessage(message_2);
        }
      });
      if (!getAgentConsent()) {
        messageHistory.addTextMessage("assistant", t.consent.question);
      }
    };
    $[88] = messageHistory;
    $[89] = options.initialMessages;
    $[90] = t.consent;
    $[91] = t.newSession;
    $[92] = t42;
  } else {
    t42 = $[92];
  }
  const startNewSession = t42;
  let t43;
  if ($[93] !== messageHistory || $[94] !== t.errorMessage) {
    t43 = async (content, preventAddTextToHistory, sendOptions) => {
      agentStore.open();
      if (!getAgentConsent()) {
        pendingConsentMessageRef.current = {
          content,
          preventAddTextToHistory,
          sendOptions
        };
        return;
      }
      setStatusStep("loading");
      resetStreaming();
      if (!preventAddTextToHistory) {
        messageHistory.addTextMessage("user", content, getAttachmentNameFromOptions(sendOptions));
      }
      const text_1 = content || (sendOptions?.formSubmission?.data ? "[File Attachment]" : "");
      if (voiceRef.current && !preventAddTextToHistory && text_1) {
        localUserTextsRef.current.push(normalizeUserText(text_1));
        if (localUserTextsRef.current.length > 10) {
          localUserTextsRef.current.shift();
        }
      }
      if (statusRef.current === "connected") {
        conversationRef.current.sendContextualUpdate(buildContextUpdate());
        conversationRef.current.sendUserMessage(text_1);
        return;
      }
      pendingMessagesRef.current.push(text_1);
      ;
      try {
        await ensureSession();
      } catch (t44) {
        const error_1 = t44;
        console.error("Error starting ElevenLabs session:", error_1);
        pendingMessagesRef.current = [];
        messageHistory.addTextMessage("assistant", t.errorMessage);
        setStatusStep(null);
      }
    };
    $[93] = messageHistory;
    $[94] = t.errorMessage;
    $[95] = t43;
  } else {
    t43 = $[95];
  }
  const sendUserMessage = t43;
  let t44;
  let t45;
  if ($[96] !== sendUserMessage) {
    t44 = () => {
      sendUserMessageRef.current = sendUserMessage;
      registerSendUserMessage(sendUserMessage);
    };
    t45 = [sendUserMessage];
    $[96] = sendUserMessage;
    $[97] = t44;
    $[98] = t45;
  } else {
    t44 = $[97];
    t45 = $[98];
  }
  useEffect(t44, t45);
  let t46;
  if ($[99] !== messageHistory || $[100] !== sendUserMessage || $[101] !== t.consent) {
    t46 = () => {
      grantAgentConsent();
      pushToDataLayer({
        event: "agent_consent_granted"
      });
      messageHistory.addSystemMessage(t.consent.granted);
      const pending = pendingConsentMessageRef.current;
      pendingConsentMessageRef.current = null;
      if (pending) {
        sendUserMessage(pending.content, pending.preventAddTextToHistory, pending.sendOptions);
      }
    };
    $[99] = messageHistory;
    $[100] = sendUserMessage;
    $[101] = t.consent;
    $[102] = t46;
  } else {
    t46 = $[102];
  }
  const grantConsent = t46;
  let t47;
  if ($[103] !== messageHistory || $[104] !== t.consent) {
    t47 = () => {
      pushToDataLayer({
        event: "agent_consent_declined"
      });
      if (hasShownDeclineHintRef.current) {
        return;
      }
      hasShownDeclineHintRef.current = true;
      messageHistory.addSystemMessage(t.consent.declinedHint);
    };
    $[103] = messageHistory;
    $[104] = t.consent;
    $[105] = t47;
  } else {
    t47 = $[105];
  }
  const declineConsent = t47;
  let t48;
  if ($[106] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = path => {
      if (statusRef.current !== "connected") {
        return;
      }
      const note = `[Seite nicht gefunden: ${path}] Diese Seite existiert nicht (404). ` + "Der Nutzer sieht gerade eine Fehlerseite. Entschuldige dich kurz, " + "verweise nicht erneut auf diese Adresse und schlage eine passende " + "Alternative vor oder frage, wonach er sucht.";
      conversationRef.current.sendContextualUpdate(buildContextUpdate());
      conversationRef.current.sendUserMessage(note);
      if (!voiceRef.current) {
        setStatusStep("loading");
      }
    };
    $[106] = t48;
  } else {
    t48 = $[106];
  }
  const reportPageNotFound = t48;
  let t49;
  let t50;
  if ($[107] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = () => {
      registerReportPageNotFound(reportPageNotFound);
    };
    t50 = [reportPageNotFound];
    $[107] = t49;
    $[108] = t50;
  } else {
    t49 = $[107];
    t50 = $[108];
  }
  useEffect(t49, t50);
  const t51 = isVoice ? conversation.status !== "connected" ? "loading" : conversation.isSpeaking ? "speaking" : "listening" : statusStep;
  const t52 = isOpen && !hasConsent;
  let t53;
  if ($[109] !== conversation.isSpeaking || $[110] !== conversation.status || $[111] !== isCollapsed || $[112] !== isNewSessionDialogOpen || $[113] !== isOpen || $[114] !== isVoice || $[115] !== messageHistory || $[116] !== t51 || $[117] !== t52) {
    t53 = {
      isOpen,
      isNewSessionDialogOpen,
      isCollapsed,
      statusStep: t51,
      connectionStatus: conversation.status,
      isSpeaking: conversation.isSpeaking,
      isVoice,
      needsConsent: t52,
      messageHistory
    };
    $[109] = conversation.isSpeaking;
    $[110] = conversation.status;
    $[111] = isCollapsed;
    $[112] = isNewSessionDialogOpen;
    $[113] = isOpen;
    $[114] = isVoice;
    $[115] = messageHistory;
    $[116] = t51;
    $[117] = t52;
    $[118] = t53;
  } else {
    t53 = $[118];
  }
  let t54;
  if ($[119] !== declineConsent || $[120] !== grantConsent || $[121] !== sendUserMessage || $[122] !== startNewSession || $[123] !== toggleVoice) {
    t54 = {
      close,
      open,
      openNewSessionDialog,
      closeNewSessionDialog,
      expand,
      startNewSession,
      sendUserMessage,
      toggleVoice,
      grantConsent,
      declineConsent
    };
    $[119] = declineConsent;
    $[120] = grantConsent;
    $[121] = sendUserMessage;
    $[122] = startNewSession;
    $[123] = toggleVoice;
    $[124] = t54;
  } else {
    t54 = $[124];
  }
  let t55;
  if ($[125] !== t53 || $[126] !== t54) {
    t55 = {
      state: t53,
      actions: t54
    };
    $[125] = t53;
    $[126] = t54;
    $[127] = t55;
  } else {
    t55 = $[127];
  }
  return t55;
};
function _temp() {}
function _temp2() {
  return {
    current_page: getCurrentPage(),
    available_sections: getAvailableSections().join(",")
  };
}
async function _temp3(voice) {
  const res = await fetch(voice ? `${TOKEN_ENDPOINT}?mode=voice` : TOKEN_ENDPOINT);
  if (!res.ok) {
    throw new Error(`Token request failed: ${res.status}`);
  }
  const credentials = (await res.json()) as SessionCredentials;
  if (voice ? !credentials.conversationToken : !credentials.signedUrl) {
    throw new Error("Incomplete credentials in token response");
  }
  return credentials;
}
function _temp4() {
  document.body.removeAttribute("data-agent-open");
}
function _temp5() {
  return agentStore.close();
}
function _temp6() {
  return agentStore.open();
}
function _temp7() {
  return agentStore.openNewSessionDialog();
}
function _temp8() {
  return agentStore.closeNewSessionDialog();
}
function _temp9() {
  return agentStore.expand();
}